import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { EAlignItems, EJustifyContent, Panel } from '@kapital-bank/kb-ui-design-system';
import { BaseLevels, BaseSizes, Button, ButtonVariant, Icon, IconName, Text, TextVariant, TextWeight } from '@kb-front/ui-kit';
import { IGiftCardInfo } from 'shared/model';
import { translate } from 'shared/utils/translation';
import GiftCardImage from 'shared/assets/images/gift-card.svg';

import GiftCardModal from './GiftCardModal';

import './index.scss';

interface IProps {
  giftInfo: IGiftCardInfo;
  giftState: boolean;
  setGiftState: Dispatch<SetStateAction<boolean>>;
  setClientGiftState: Dispatch<SetStateAction<boolean>>;
}

const GiftCard: FC<IProps> = ({ giftInfo, giftState, setGiftState, setClientGiftState }) => {
  const [isVisibleGiftCardModal, setIsVisibleGiftCardModal] = useState(false);
  const giftInfoAdvantage = [giftInfo?.serviceFee, giftInfo?.bonus];

  const getInfoModalData = useMemo(() => {
    return [
      {
        name: translate('scoring.giftMoreInfoModal.creditLimit'),
        value: giftInfo?.creditLimit,
      },
      {
        name: translate('scoring.giftMoreInfoModal.serviceFee'),
        value: giftInfo?.serviceFee,
        isFree: true,
      },
      {
        name: translate('scoring.giftMoreInfoModal.partners'),
        value: giftInfo?.partners,
      },
      {
        name: translate('scoring.giftMoreInfoModal.gracePeriod'),
        value: giftInfo?.gracePeriod,
      },
      {
        name: translate('scoring.giftMoreInfoModal.bonus'),
        value: giftInfo?.bonus,
      },
    ];
  }, [giftInfo]);

  return (
    <Panel className="gift__card" padding={3}>
      <Panel className="gift__card__info" marginBottom={3}>
        <Panel className="gift__card__info__text-wrapper">
          <Text weight={TextWeight.SEMI_BOLD} level={BaseLevels.L2} block>
            {giftInfo?.title}
          </Text>
          {giftInfoAdvantage?.map(item => (
            <div className="gift__card__info__text-wrapper__percent" key={item}>
              <div className="success-checked">
                <Icon iconName={IconName.CHECK} size={BaseSizes.M} variant={TextVariant.SUCCESS} />
              </div>
              <Text variant={TextVariant.SECONDARY} level={BaseLevels.L3}>
                {item}
              </Text>
            </div>
          ))}
          <Panel marginTop={2}>
            <Text variant={TextVariant.SECONDARY} weight={TextWeight.SEMI_BOLD}>
              <span className="details">
                <Text variant={TextVariant.LINK} onClick={() => setIsVisibleGiftCardModal(true)}>
                  {translate('scoring.giftCard.cashbackCardMore')}{' '}
                </Text>
              </span>
              {translate('scoring.giftCard.cashbackCardDetails')}{' '}
              <span className="details">
                <Text variant={TextVariant.LINK} onClick={() => setIsVisibleGiftCardModal(true)}>
                  {translate('scoring.giftCard.cashbackCardMoreDetails')}{' '}
                </Text>
              </span>
            </Text>
          </Panel>
        </Panel>
        <Panel>
          <img src={GiftCardImage} alt="gift-card" />
        </Panel>
      </Panel>
      <div className="gift__card__border" />
      <Panel className="gift__card-added">
        {giftState ? (
          <>
            <Panel justifyContent={EJustifyContent.CENTER} alignItems={EAlignItems.CENTER}>
              <div className="gift__card__added-checked">
                <Icon iconName={IconName.CHECK} size={BaseSizes.L} variant={TextVariant.SUCCESS} />
              </div>
              <Text variant={TextVariant.DEFAULT} level={BaseLevels.L2}>
                {translate('scoring.giftCard.successedCard')}
              </Text>
            </Panel>
            <Button
              variant={ButtonVariant.GHOST}
              onClick={() => {
                setGiftState(false);
                setClientGiftState(false);
              }}
            >
              {translate('scoring.giftCard.deleteCard')}
            </Button>
          </>
        ) : (
          <Button
            variant={ButtonVariant.GHOST}
            block
            onClick={() => {
              setGiftState(true);
              setClientGiftState(true);
            }}
          >
            {translate('scoring.giftCard.addCard')}
          </Button>
        )}
      </Panel>

      <GiftCardModal
        getInfoModalData={getInfoModalData}
        setIsVisibleGiftCardModal={setIsVisibleGiftCardModal}
        isVisibleGiftCardModal={isVisibleGiftCardModal}
      />
    </Panel>
  );
};

export default GiftCard;
